import { GROUP_MEMBERS_MAX } from "@edu/shared";
import type { GroupScheduleSkipView, OccurrenceLifecycle } from "@edu/shared";
import { lessonCountText, studentCountText } from "../../lib/plurals.ts";

/**
 * Слова экранов группового расписания: запертое повторение, причины неактивной
 * группы, цена массовых действий и состав без серий.
 *
 * Чистые функции с тестами, а не строки по месту, по той же причине, что у слов
 * групп (`features/groups/groups-text.ts`) и у панели «Где дыры»
 * (`features/students/student-skills-text.ts`): здесь считаются числа, которые
 * нельзя писать на глаз, и собираются фразы, которые обязаны совпадать с тем,
 * чем откажет сервер. Разъехавшись, они дают отказ там, где кнопка была активна.
 *
 * ⚠️ **Подписи «6 учеников» под названием группы здесь нет и заводить её не
 * надо.** В сетке число участников показывается значком с числом, а не словом
 * (решение владельца 30.08.2026, правило целиком в
 * `docs/cabinet-tables-design.md`, раздел 8б). Слово остаётся ровно там, где
 * значок сказал бы не то же самое: в подтверждении отмены и переноса, потому что
 * там человеку надо понять цену, а не опознать категорию.
 *
 * ⚠️ **Ни одного числа внутри самих строк.** Потолок берётся из
 * `GROUP_MEMBERS_MAX`, размер группы приезжает аргументом, а форму
 * числительного выбирает общий помощник (`lib/plurals.ts`). Зашитая двенадцатка
 * соврала бы молча в день, когда потолок поменяют, и это проверяется тестом, а
 * не подразумевается.
 *
 * ⚠️ **Собирательных числительных («у шестерых») здесь нет.** Их нет в
 * `lib/plurals.ts`, они потребовали бы новой морфологии ради одного места и
 * ломались бы на единице, а единица законна: группа, из которой ушли все кроме
 * одного, даёт занятие на одного человека.
 *
 * ⚠️ **Родительный падеж после предлога тут невыразим, и это не придирка.**
 * `studentCountText` даёт именительный («6 учеников», «2 ученика»), поэтому
 * «отменить занятие у 2 ученика» получилось бы само собой. Отсюда двоеточие во
 * всех фразах со счётом: число стоит отдельной группой слов и падежа соседей не
 * трогает. Та же ловушка разобрана у `receiptText` в
 * `features/announcements/announcement-text.ts`.
 *
 * ⚠️ Охраняет это не двоеточие, а тест, и запрещает он саму форму: предлог,
 * которому подчинено число со словом («у 2 ученика»), роняет прогон в любой
 * строке этого файла. До ревью 30.08.2026 двоеточие можно было убрать, и весь
 * набор оставался зелёным.
 */

/**
 * Строка под запертым переключателем повторения в диалоге создания занятия.
 *
 * ⚠️ Разового занятия у группы нет (решение владельца 30.08.2026, раздел 9
 * `docs/group-lessons-design.md`), и видно это запертым переключателем, а не
 * отказом на кнопке. Строка обязана сказать вторую половину: где разовое всё же
 * бывает. Без неё человек, пришедший поставить группе дополнительное занятие
 * перед пробником, узнаёт только запрет и не узнаёт, что делать.
 *
 * Слова взяты с самого экрана («разовое занятие», «каждую неделю»): это не
 * указание пальцем на соседний элемент, а ответ на вопрос, который человек задал
 * нажатием.
 *
 * ⚠️ Запрет объясняет ПЕРВАЯ фраза, а не вторая. Без него строка описывает то,
 * что человек и так видит выбранным, и лишает лишнее нажатие всякого смысла.
 *
 * ⚠️ **«Только каждую неделю» отсюда убрано** (правка по итогам ревью
 * 30.08.2026): это читается как «одно занятие в неделю», а у группы их бывает
 * несколько, вторник и четверг это обычное расписание. Считает запертый
 * переключатель теперь слово «всегда», и второго прочтения у него нет.
 */
export const GROUP_WEEKLY_ONLY =
  "У группы занятия всегда повторяются каждую неделю. Разовое занятие можно поставить одному ученику.";

/**
 * Почему этой группе расписание не завести, либо `null`.
 *
 * Причин четыре, и порядок тот же, что у сервера
 * (`routes/schedule/group-series.ts`, `POST .../series`): архив, уже заведённое
 * расписание, слишком много людей, некому заниматься. Считается это ДО нажатия,
 * теми же числами, какими откажет сервер: молча не заведённое расписание это
 * молчаливая деградация (5.5 CLAUDE.md), а неактивный вариант с названной
 * причиной это то же самое, сказанное вовремя.
 *
 * ⚠️ **Потолок считается по ВСЕМ участникам, включая отвязанных.** Так считает
 * сервер, и довод записан там же: отвязанный остаётся в составе и завтра
 * вернётся перевыпуском ссылки, а потолок, посчитанный по сегодняшней явке, в
 * этот день молча перерастёт двенадцать.
 *
 * ⚠️ **Строка про уже заведённое расписание звучит путём, а не отказом**, и это
 * требование, а не стиль: диалог не заводит второго расписания и не добавляет
 * день, он ведёт на экран расписания группы, где написана зона пучка. Человек
 * пришёл поставить день, и фраза обязана сказать, где этот день ставится.
 * Ссылка туда подписывается `OPEN_GROUP_SCHEDULE`.
 *
 * ⚠️ Причина «некому заниматься» разведена на два случая. Пустая группа и
 * группа, где у всех отозваны ссылки, лечатся разным, а сервер отвечает на них
 * одним кодом `group_schedule_empty`: «в группе никого нет» человеку с восемью
 * учениками в составе выглядело бы поломкой экрана.
 *
 * Возвращается строчная часть фразы: она встаёт в скобки после названия группы,
 * и собирает это `groupOptionLabel`.
 *
 * ⚠️ **Все четыре причины это состояния, ни одна не команда** (правка по итогам
 * ревью 30.08.2026). «Новый день добавьте в нём» внутри скобок после названия
 * группы читалось странно: в списке выбора человеку называют, чем эта строка
 * отличается от соседних, а не велят что-то сделать. Путь при этом сохранён,
 * место названо, и подписывает его `OPEN_GROUP_SCHEDULE`.
 */
export function groupScheduleBlockedText(args: {
  archived: boolean;
  hasSchedule: boolean;
  memberCount: number;
  activeMemberCount: number;
}): string | null {
  if (args.archived) return "в архиве";
  if (args.hasSchedule) return "расписание уже заведено, новый день добавляется в нём";
  if (args.memberCount > GROUP_MEMBERS_MAX) {
    return `в группе ${studentCountText(args.memberCount)}, а расписание можно завести не больше чем на ${String(GROUP_MEMBERS_MAX)}`;
  }
  if (args.memberCount === 0) return GROUP_EMPTY;
  if (args.activeMemberCount === 0) return "ссылки отозваны у всех в группе";
  return null;
}

/** ⚠️ Одна мысль одной редакцией: состав писал «В группе пока никого», а список
 * выбора «в группе пока никого нет» (ревью 30.08.2026). */
const GROUP_EMPTY = "в группе пока никого нет";

/**
 * Название группы в списке выбора, с причиной у неактивной.
 *
 * Скобки ставятся здесь, а не на каждом экране: два места сложат их по-разному в
 * тот же день, когда причин станет пять.
 */
export function groupOptionLabel(title: string, blocked: string | null): string {
  return blocked === null ? title : `${title} (${blocked})`;
}

/**
 * Та же причина там, где название группы стоит заголовком экрана:
 * `groupOptionLabel` дал бы «8Б класс» сверху и «8Б класс (в архиве)» ниже.
 * Строчный кусок становится фразой: «в архиве» это «В архиве.».
 */
export function groupScheduleBlockedLine(blocked: string): string {
  return `${blocked.slice(0, 1).toUpperCase()}${blocked.slice(1)}.`;
}

/** Состав пустой, отдельной строкой: та же причина, тем же сбором. */
export const GROUP_EMPTY_LINE = groupScheduleBlockedLine(GROUP_EMPTY);

/** Подпись ссылки на экран расписания группы. */
export const OPEN_GROUP_SCHEDULE = "Открыть расписание группы";

/**
 * Расписание достанется не всем: сказано ДО нажатия.
 *
 * ⚠️ **Числа считаются, а не пишутся**, и второе из них берётся из состава
 * группы, а первое из числа живых пар (`activeMemberCount`). Без этой строки цену
 * отказа человек узнаёт списком невзятых уже после того, как расписание завелось,
 * то есть тогда, когда выбирать поздно.
 *
 * ⚠️ Имён здесь нет намеренно, хотя после нажатия они появляются
 * (`scheduleSkippedLines`). До нажатия вопрос другой: «всем ли достанется», и
 * список из семи имён на него отвечает хуже, чем два числа.
 *
 * `null` означает «подписи нет вовсе»: расписание получают все, либо не получает
 * никто, и второй случай называет уже `groupScheduleBlockedText`.
 */
export function partialScheduleText(active: number, total: number): string | null {
  if (active <= 0 || total <= 0 || active >= total) return null;
  return `Расписание получат не все: ${studentCountText(active)} из ${String(total)}. У остальных отозвана ссылка, а без неё новых занятий не назначить.`;
}

/**
 * Кого расписание не застало, словами. По строке на причину.
 *
 * ⚠️ **Из группового расписания никто не выпадает молча** (5.5 CLAUDE.md), и это
 * то же правило, по которому названы пропущенные у рассылки
 * (`features/announcements/teacher/skipped-text.ts`). Оттуда же взяты два
 * приёма, и оба нужны здесь по тем же причинам:
 *
 * - **безличная форма**: «не получила Маша» и «не получили Маша и Петя» это
 *   разные слова, а «расписания не досталось» одинаково верно и для одного, и
 *   для семерых, и не требует знать, кто из них какого пола;
 * - **запасной счёт**: имя приходит не из ответа сервера, а из карты, которую
 *   диалог собрал сам, и в ней может не оказаться ученика, добавленного в группу
 *   из другой вкладки. Тогда человек всё равно узнаёт, что достались не все:
 *   это важнее точного имени. Пустое имя из одних пробелов сюда попадает по той
 *   же дыре, что описана у рассылки (`DisplayName` не обрезает края).
 *
 * ⚠️ **Причина второй строкой, а не пересказом первой.** Сегодня при заведении
 * расписания приезжает только `student_not_linked`, а `no_upcoming_occurrence`
 * приходит с добавления участника в группу, и его слова стоят в
 * `features/groups/groups-text.ts`. Обе ветки описаны здесь всё равно:
 * перечисление обязано ронять сборку, когда причин станет три, а не тихо уводить
 * человека в общую фразу.
 *
 * ⚠️ **Это близнец `skippedLines` у рассылки, и общего кода у них нет
 * намеренно.** Обе находки ревью 30.08.2026 («Ссылка отозвана» под тремя
 * именами и «ещё 2» без единого имени) перенесены к близнецу тем же кругом,
 * поэтому механика у них снова одна: группировка по причине, форма причины по
 * числу людей и запасной счёт безымянных. Разными остались ровно те две вещи,
 * ради которых их и разводили: сами слова и источник имени (здесь карта,
 * которую собрал диалог, там ответ сервера). Общий помощник пришлось бы вынести
 * в `lib/` (импорт из чужой фичи в проекте не заведён ни разу), и он свёлся бы
 * к пяти строкам группировки, а всё, что читает человек, осталось бы разным.
 * **Правя одно место, откройте второе:** `announcements/teacher/skipped-text.ts`.
 */
const SKIP_REASON_TEXT: Record<GroupScheduleSkipView["reason"], (count: number) => string> = {
  /**
   * ⚠️ Ссылок столько же, сколько людей: «Ссылка отозвана» под тремя именами это
   * неправда, которую читатель проверит взглядом на строку выше. Формы всего
   * две, и числительное тут ни при чём: на двадцати одном человеке ссылок
   * по-прежнему много. Ту же форму держит соседняя строка этого файла, «ссылки
   * отозваны у всех в группе».
   */
  student_not_linked: (count) => (count === 1 ? "Ссылка отозвана." : "Ссылки отозваны."),
  no_upcoming_occurrence: () => "Ближайшее занятие у группы не назначено.",
};

export function scheduleSkippedLines(
  skipped: readonly GroupScheduleSkipView[],
  nameOf: (studentUserId: string) => string | undefined,
): string[] {
  const byReason = new Map<GroupScheduleSkipView["reason"], string[]>();
  const unnamed = new Map<GroupScheduleSkipView["reason"], number>();

  for (const s of skipped) {
    const name = (nameOf(s.studentUserId) ?? "").trim();
    if (name === "") {
      unnamed.set(s.reason, (unnamed.get(s.reason) ?? 0) + 1);
      continue;
    }
    const names = byReason.get(s.reason) ?? [];
    names.push(name);
    byReason.set(s.reason, names);
  }

  const lines: string[] = [];
  for (const reason of new Set(skipped.map((s) => s.reason))) {
    const names = byReason.get(reason) ?? [];
    const rest = unnamed.get(reason) ?? 0;
    /*
      ⚠️ Ветка «имён нет вовсе» отдельная, и случай этот штатный, а не редкий:
      имя берётся из карты, которую диалог собрал сам, и ученика, добавленного в
      группу из другой вкладки, в ней нет ни одного. «Расписания не досталось:
      ещё 2» это «ещё» без того, к чему оно «ещё», поэтому без имён строка
      называет счёт. Число стоит после двоеточия, где именительный падеж законен,
      как и везде в этом файле.
    */
    const who =
      names.length === 0
        ? studentCountText(rest)
        : [...names, ...(rest > 0 ? [`ещё ${String(rest)}`] : [])].join(", ");
    lines.push(`Расписания не досталось: ${who}. ${SKIP_REASON_TEXT[reason](names.length + rest)}`);
  }
  return lines;
}

/**
 * Пометка участника, у которого нет серии в пучке.
 *
 * ⚠️ **Разница множеств считается на клиенте**: состав приезжает с карточки
 * группы, а получившие серию с расписания, и на экране это единственное место,
 * где видно человека, который в группе числится, а занятий не получает. Секции
 * невзятых на экране нет и быть не может: `skipped` заполняется ровно при
 * создании пучка, а открытый экран показывал бы пустоту всегда.
 *
 * ⚠️ **Причина здесь НЕ называется, и это осознанно.** Причин две (ссылка была
 * отозвана, когда расписание заводили, либо человек пришёл, когда ближайшего
 * занятия по расписанию уже не нашлось), а разность множеств не отличает одну от
 * другой. Назвать одну из двух значит соврать половине смотрящих, и хуже всего
 * это на подсказке, которую читают как объяснение.
 *
 * ⚠️ **Слова, а не только цвет.** Приглушённая строка ничего не говорит ни
 * читалке, ни печати, ни человеку с нарушением цветовосприятия, и тот же довод
 * записан у `MEMBER_NAME_UNSET_TITLE` в словах групп.
 *
 * ⚠️ **Речь про одного человека, а не про группу** (правка по итогам ревью
 * 30.08.2026). Стояло «а занятий по расписанию группы нет», и стоит эта
 * подсказка на экране, где расписание группы открыто и занятия в нём видны:
 * читателю мы отрицали ровно то, на что он смотрит. Слова взяты у той же нужды
 * в `features/groups/groups-text.ts` (`NO_UPCOMING_OCCURRENCE_TEXT`), где
 * человека называют по имени, а занятий нет именно у него.
 *
 * ⚠️ **Первой половины больше нет** (ревью 30.08.2026): стояло «Этот ученик в
 * группе, но занятий…», а панель, где висит подсказка, вся и есть «кто в группе».
 */
export const MEMBER_WITHOUT_SERIES = {
  text: "занятий нет",
  title: "Занятий в расписании для него пока нет",
} as const;

/**
 * ⚠️ **Здесь причина как раз НАЗЫВАЕТСЯ**, в отличие от соседней пометки: там
 * причин две и разность множеств их не различает, а тут признак приезжает полем
 * `unlinked`. Без пометки рисовалось «Занятия с 1 сен», хотя занятия такой пары
 * обрываются датой отвязки. Выход назван потому, что он один: ссылку
 * перевыпускают на карточке ученика, а имя в списке туда и ведёт.
 */
export const MEMBER_UNLINKED = {
  text: "ссылка отозвана",
  title: "Пока ссылка отозвана, занятий у этого ученика не будет. Вернуть их можно новой ссылкой",
} as const;

/**
 * У группы нет расписания: два обычных случая, а не ошибка.
 *
 * `GET .../series` отвечает 404, когда расписания не заводили и сразу после
 * роспуска на этом же экране. Различать их экрану нечем, да и незачем: делать в
 * обоих случаях надо одно и то же.
 *
 * ⚠️ Первая фраза говорит, что расписание у группы это единственный способ
 * ставить ей занятия, а не повторяет заголовок другими словами. Вторая называет
 * исход нажатия, и обе стороны названы: занятия видны и преподавателю, и
 * ученикам.
 *
 * ⚠️ **Команды во второй фразе нет** (правка по итогам ревью 30.08.2026). Было
 * «Заведите его, и они появятся и у вас, и у учеников»: команда показывала
 * пальцем на кнопку, стоящую тут же (правило 2 скилла `teacher-texts`), два
 * местоимения подряд означали разное («его» это расписание, «они» это занятия),
 * а три «и» в одной фразе вслух не произносятся.
 *
 * ⚠️ **«У этой группы» убрано** (ревью 30.08.2026): название стоит заголовком.
 */
export const NO_GROUP_SCHEDULE = {
  title: "Расписания пока нет",
  text: "Занятия у группы идут только по расписанию. Когда оно появится, занятия будут видны и вам, и ученикам.",
  action: "Завести расписание",
} as const;

/**
 * Цена массового действия словами: занятие снимается и переезжает у всех разом.
 *
 * ⚠️ **Здесь остаётся СЛОВО, хотя в строке сетки значок его заменил.** Правило
 * 8б `docs/cabinet-tables-design.md` само себя и ограничивает: значком
 * заменяется подпись, называющая категорию, а не предупреждение. Человеку тут
 * надо понять причину осторожности, а не опознать вид занятия.
 *
 * ⚠️ **Число это размер занятия, а не размер группы.** Приезжает оно строкой
 * сетки (`size`), где считаются те, кому занятие действительно производится:
 * отвязанная пара занятий не даёт, и обещать ей отмену незачем. Поле объявлено
 * положительным, поэтому единица законна, и фраза обязана читаться на ней: «у
 * всех в группе: 1 ученик» это правда про группу, из которой ушли все остальные.
 *
 * ⚠️ **Подписи кнопок здесь нет, и заводить её не надо.** Формы отмены и
 * переноса у группы те же самые, что у одиночного занятия, отличается адрес
 * ручки; второе слово для того же действия читалось бы как другое действие.
 * Цену называет эта строка, она встаёт над кнопкой подтверждения.
 *
 * ⚠️ **Фраза начинается с занятия, а не с отглагольного существительного**
 * (правка по итогам ревью 30.08.2026). «Отмена снимет занятие у всех в группе»
 * и «Перенос сдвинет занятие» вслух никто не произносит, а над кнопкой
 * подтверждения человек читает именно вслух, про себя. Событие называет само
 * занятие: оно снимется, оно переедет.
 */
export function cancelGroupOccurrenceText(students: number): string {
  return `Занятие снимется у всех в группе: ${studentCountText(students)}.`;
}

export function moveGroupOccurrenceText(students: number): string {
  return `Занятие переедет у всех в группе: ${studentCountText(students)}.`;
}

/**
 * То же самое, но уже случившееся: перенос перетаскиванием цену называет ПОСЛЕ.
 *
 * ⚠️ Своей фразой, а не переписанным временем у соседней: у формы ступень «до»
 * осталась, и обе фразы обязаны жить рядом, чтобы правка одной не молчала о
 * второй. Разбор решения в шапке `DropToast.tsx`.
 */
export function movedGroupOccurrenceText(students: number): string {
  return `Переехало у всех в группе: ${studentCountText(students)}.`;
}

/**
 * Цена правки дня: уроки, которые останутся без своего занятия.
 *
 * ⚠️ **Число считается, а не пишется**, и приезжает сводным по всей группе:
 * уроков столько же, сколько учеников с подготовленным уроком, а дней обычно
 * один, поэтому дни перечисляются отдельно. ⚠️ Фраза начинается с уроков, а не
 * с правки: стояло «Правка снимает уже подготовленное: 6 уроков» (30.08.2026).
 */
export function orphanedLessonsText(lessons: number): string {
  return `Уроки к этим дням уже подготовлены: ${lessonCountText(lessons)}. Они никуда не денутся, но останутся без своего занятия и будут видны в расписании отдельными строками. Вот эти дни:`;
}

/**
 * ⚠️ **Зона названа значением, а не «этой»**: время дня пишется в зоне
 * РАСПИСАНИЯ, а не смотрящего, и разошедшиеся зоны обязано быть видно. А вот
 * объяснение, чья это зона, стоит один раз и у поля пояса: две панели про одно
 * читаются как две разные мысли.
 */
export function slotsTimeZoneText(timeZone: string): string {
  return `Время каждого дня записано в зоне ${timeZone}.`;
}

/**
 * Роспуск: единственное необратимое действие экрана, и до переезда сюда
 * единственные слова фичи без теста (ревью 30.08.2026). ⚠️ Что НЕ пропадает,
 * сказано до ПЕРВОГО нажатия, а не в подтверждении.
 */
export const DISBAND_SCHEDULE = {
  title: "Если занятия закончились",
  text: "Занятий по расписанию больше не будет. Прошлые остаются на месте вместе с уроками и досками, а новое расписание можно завести здесь же.",
  action: "Распустить расписание",
  confirm: "Да, распустить",
  cancel: "Отмена",
} as const;

/**
 * ⚠️ «Откройте страницу заново» здесь не годится, хотя ровно так говорит общий
 * словарь отказов: экран переходит в «расписания пока нет» сам.
 */
export const SCHEDULE_GONE = "Расписание уже распустили в другой вкладке.";

/**
 * Комната у занятия есть, а войти в неё нельзя: имена в группе скрыты.
 *
 * ⚠️ **Это не то же самое, что «комнаты нет»** (раздел 5
 * `docs/group-room-design.md`): состав в признак `roomAvailable` не входит
 * намеренно, иначе отказ `group_members_hidden` стал бы недостижим из
 * интерфейса, а тест на него охранял бы мёртвый путь. Поэтому текст стоит РЯДОМ
 * с кнопкой, а кнопка остаётся на месте.
 *
 * ⚠️ Первая фраза дословно та же, что в словаре отказов (`common.ts`,
 * `group_members_hidden`): предпроверка и отказ обязаны говорить одно и то же,
 * иначе человек решит, что это две разные беды.
 *
 * ⚠️ Про внешнюю ссылку тут не сказано ни слова, хотя проект предлагал: она
 * бывает и пустой (раздел 6 CLAUDE.md), и тогда фраза «занятие идёт по внешней
 * ссылке» была бы неправдой ровно там, где человеку и так нечем провести урок.
 */
export const MEMBERS_HIDDEN = "Пока имена в группе скрыты, в общую комнату не войти.";

export const MEMBERS_HIDDEN_NOTE =
  `${MEMBERS_HIDDEN} В ней ученики видят имена друг друга и слышат голоса.`;

/*
  ⚠️ Подписи «Открыть карточку группы» здесь больше нет (снята 03.09.2026).
  Карточка занятия вела туда двумя путями сразу: этой ссылкой и названием
  группы в заголовке, которое стало ссылкой в тот же день. Двух дверей в одно
  место в одной карточке быть не должно, а название видно всегда, тогда как
  строка про скрытый состав приезжает редко.
*/

/**
 * Состояния, в которых дверь в комнату ещё открывается.
 *
 * ⚠️ Список нужен затем, чтобы текст не приезжал на прошедшее занятие: там он
 * звал бы открыть состав ради комнаты, в которую уже не войти, то есть просил бы
 * раскрыть имена детей ни за чем.
 */
const ENTRY_AHEAD = new Set<OccurrenceLifecycle>(["scheduled", "open", "ready", "live"]);

/**
 * Показывать ли этот текст у занятия.
 *
 * Чистая функция, а не три условия в разметке: их порядок и есть правило, а
 * повторить его во втором месте однажды придётся.
 */
export function membersHiddenNote(args: {
  roomAvailable: boolean;
  /** Поля `group` нет у строки ученика вовсе, и тогда текст не его. */
  membersVisible: boolean | undefined;
  state: OccurrenceLifecycle | null;
}): string | null {
  if (!args.roomAvailable || args.membersVisible !== false) return null;
  if (args.state === null || !ENTRY_AHEAD.has(args.state)) return null;
  return MEMBERS_HIDDEN_NOTE;
}
